// Shapes of crm-billing's catalog operations (internal/billing/catalog.go), as the admin reads them.

export interface ProductSettings {
  headline?: string;
  thank_you_url?: string;
  product_param?: string;
  terms_url?: string;
  purchase_tag_ids?: number[];
  access_tag_ids?: number[];
  alert_emails?: string[];
  /** Dormant: PayPal is switched off (Brandon, 2026-10-27). Not shown; kept so saving settings doesn't drop it. */
  allow_paypal?: boolean;
  quantity?: boolean;
  access_days?: number;
  shipping_level?: string;
  no_affiliate?: boolean;
  /** Everything else billing keeps here (after-purchase content set through the MCP, Lulu, rejoin URL…):
   *  the admin never edits these, and saving the checkout settings keeps them as they are on the server. */
  [other: string]: unknown;
}

/** The settings the "Checkout and after purchase" form edits; saving changes only these. */
export const FORM_SETTINGS = [
  "headline",
  "thank_you_url",
  "product_param",
  "terms_url",
  "purchase_tag_ids",
  "access_tag_ids",
  "alert_emails",
  "access_days",
  "quantity",
  "no_affiliate",
] as const;

const isEmpty = (v: unknown) => v === "" || v === undefined || v === null || v === false || v === 0 || (Array.isArray(v) && v.length === 0);

/** billing's products_update replaces ALL settings, so the form's values are laid over the server's latest copy
 *  (read just before saving): content Claude changed meanwhile isn't overwritten, and empty form fields are removed. */
export function mergeFormSettings(latest: ProductSettings, form: ProductSettings): ProductSettings {
  const out: Record<string, unknown> = { ...latest };
  for (const k of FORM_SETTINGS) {
    const v = form[k];
    if (isEmpty(v)) delete out[k];
    else out[k] = v;
  }
  return out as ProductSettings;
}

export interface Price {
  id: number;
  product_id: number;
  name: string;
  type: "one_time" | "recurring" | "payment_plan" | string;
  amount_cents: number;
  interval: string | null;
  interval_count: number | null;
  trial_days: number;
  down_payment_cents: number | null;
  installments: number | null;
  keap_id: number | null;
  is_default: boolean;
  active: boolean;
  summary: string;
}

export interface Product {
  id: number;
  brand_id: number;
  name: string;
  kind: string;
  slug: string | null;
  sku: string | null;
  description: string | null;
  requires_shipping: boolean;
  access_keys: string[] | null;
  active: boolean;
  archived_at: string | null;
  keap_id: number | null;
  prices: Price[];
  settings_parsed: ProductSettings;
  checkout_url?: string;
  lulu: unknown;
  created_at: string;
  updated_at: string;
}

export interface PromoCode {
  id: number;
  promotion_id: number;
  code: string;
  max_redemptions: number | null;
  redemptions: number;
  active: boolean;
}

export interface Promotion {
  id: number;
  keap_id: number | null;
  brand_id: number | null;
  name: string;
  description: string | null;
  discount_type: "percent" | "amount" | "price_override" | "free_shipping" | string;
  /** percent: hundredths of a percent (2500 = 25%); amount / price_override: cents. */
  discount_value: number;
  applies_to_product_ids: number[] | null;
  min_subtotal_cents: number | null;
  starts_at: string | null;
  ends_at: string | null;
  max_redemptions: number | null;
  per_contact_limit: number | null;
  active: boolean;
  codes: PromoCode[];
  summary: string;
}

export interface AccessKey {
  key: string;
  brand_id: number | null;
  name: string;
  description: string | null;
}

export const PRODUCT_KINDS: [string, string][] = [
  ["course", "Course"],
  ["book", "Book"],
  ["event", "Event"],
  ["membership", "Membership"],
  ["service", "Service"],
  ["digital", "Digital"],
  ["physical", "Physical"],
  ["bundle", "Bundle"],
  ["other", "Other"],
];
export const kindLabel = (k: string) => PRODUCT_KINDS.find(([x]) => x === k)?.[1] ?? k;

export const PRICE_TYPES: [string, string][] = [
  ["one_time", "One payment"],
  ["recurring", "Subscription"],
  ["payment_plan", "Payment plan"],
];
export const priceTypeLabel = (t: string) => PRICE_TYPES.find(([x]) => x === t)?.[1] ?? t;

export const INTERVALS: [string, string][] = [
  ["month", "month"],
  ["year", "year"],
  ["week", "week"],
  ["day", "day"],
];
